<template>
    <div class="tw:grid tw:gap-4">
        <div>
            <label class="label">Title</label>

            <e-input
                v-model="title"
                :placeholder="`Enter your new ${formatType()} title here...`"
                @input="handleTitleInput"
                @blur="handleTitleBlur"
            />
        </div>

        <div>
            <label class="label">Questions per Page</label>

            <e-input v-model="numberOfQuestions" type="number" placeholder="1, 2, 3.." />
        </div>

        <template v-if="quiz">
            <div>
                <label class="label">Groups</label>

                <group-select v-if="$can('create', 'quiz group')" :already-selected="quiz.groups" @save="syncGroups" @remove="removeGroups" />
                <meta-groups v-else-if="quiz.groups && quiz.groups.length > 0" :groups="quiz.groups" />
            </div>

            <div>
                <label class="label">Tags</label>

                <tag-switcher
                    :tags="safeAssignableTags"
                    :subject="quiz"
                    :tag-type="'quiz'"
                    :action="'Quizzes/updateTags'"
                />
            </div>

            <div>
                <label class="label">Course Codes</label>

                <course-code-select
                    :already-selected="courseCodes"
                    @save="syncCourseCodes"
                    @remove="removeCourseCodes"
                />
            </div>
        </template>

        <div class="tw:flex tw:flex-col tw:gap-2">
            <e-checkbox v-model="randomQuestionOrder">
                Arrange Questions in Random Order on Every
                {{ formatType(true) }} Attempt
            </e-checkbox>

            <e-checkbox v-model="answerInOrder">Student Must Submit Answers in Order</e-checkbox>

            <div v-if="formatType(true) === 'Quiz'" class="tw:flex tw:items-center">
                <e-checkbox v-model="calculatorEnabled">
                    Enable calculator settings on a
                    {{ formatType(true) }} activity
                </e-checkbox>
                <e-tooltip :label="translate('enable_calculator')" multilined position="is-right">
                    <e-icon icon="info-circle" class="has-text-info" />
                </e-tooltip>
            </div>
        </div>

        <draggable
            v-if="questions.length"
            v-bind="dragOptions"
            :list="questions"
            item-key="uuid"
            handle=".media-draggable__draghandle"
            tag="transition-group"
            :component-data="{
                type: 'transition',
            }"
            revert-on-spill
            :remove-on-spill="false"
            @end="handleDrop"
        >
            <template #item="{ element: question }">
                <div class="card-list">
                    <quiz-questions
                        :uuid="uuid"
                        :order="question.display_order"
                        :is-quiz-ai-quiz-generation-result="isQuizAiQuizGenerationResult"
                        @incomplete-question="validatePreview"
                    />
                </div>
            </template>
        </draggable>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import { createNamespacedHelpers } from 'vuex';

import MetaGroups from '@/components/meta-panel/MetaGroups.vue';
import CourseCodeSelect from '@/components/modal-select/CourseCodeSelect.vue';
import GroupSelect from '@/components/modal-select/GroupSelect.vue';
import QuizQuestions from '@/components/quiz-builder/quiz-questions/QuizQuestions.vue';
import TagSwitcher from '@/components/switcher/TagSwitcher.vue';
import QuizTypeMixin from '@/mixins/QuizType.mixin.js';

const { mapState } = createNamespacedHelpers('QuizzesView');

const field = fieldName => {
    return {
        get() {
            return this.get(fieldName);
        },

        set(value) {
            this.set({ field: fieldName, value });
        },
    };
};

export default {
    name: 'QuizEditorBody',

    components: {
        CourseCodeSelect,
        QuizQuestions,
        draggable,
        GroupSelect,
        TagSwitcher,
        MetaGroups,
    },

    mixins: [QuizTypeMixin],

    props: {
        uuid: {
            type: [String, Number],
            required: true,
        },
        isNew: {
            type: Boolean,
            required: false,
        },
        isQuizAiQuizGenerationResult: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['has-questions'],

    data: () => ({
        dragOptions: {
            animation: 200,
            group: 'questions',
            disabled: false,
            ghostClass: 'is-ghost',
        },
        quiz: null,
    }),

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    computed: {
        ...mapState(['assignableTags']),

        answerInOrder: field('must_answer_in_order'),

        id: field('id'),

        numberOfQuestions: field('questions_per_page'),

        questions() {
            const questionsData = this.get('questions');
            // Ensure we always return an array to prevent undefined errors
            return Array.isArray(questionsData) ? questionsData : [];
        },

        randomQuestionOrder: field('random_question_order'),

        title: {
            get() {
                const titleValue = this.get('title');
                // Ensure we return empty string for null/undefined values
                return titleValue || '';
            },
            set(value) {
                // Ensure value is a string before calling trim
                const stringValue = String(value || '');
                const trimmedValue = stringValue.trim();
                this.set({ field: 'title', value: trimmedValue });
            },
        },

        type: field('type'),

        courseCodes: field('course_codes'),

        calculatorEnabled: field('calculator_enabled'),

        hasUnsavedQuestions() {
            return this.$store.getters[`QuizBuilder/${this.uuid}/hasUnsavedQuestions`]();
        },

        safeAssignableTags() {
            return Array.isArray(this.assignableTags) ? this.assignableTags : [];
        },
    },

    watch: {
        id(value) {
            if (value) {
                this.quiz = this.$store.getters['QuizBuilder/get'](this.uuid);
            }
        },

        hasUnsavedQuestions(value) {
            this.dragOptions.disabled = value;
        },
    },

    async created() {
        // this.quiz is needed for groups/tags, reference QuizBuilder store module for other data needs
        this.quiz = this.$store.getters['Quizzes/get'](this.id);

        if (!this.quiz && this.id) {
            await this.$store.dispatch('QuizzesView/fetchAssignableTags');

            this.quiz = await this.$store.dispatch('Quizzes/fetchOne', this.id);
        }

        this.$emit('has-questions', !!this.questions.length);

        if (this.formatType(true) === 'Survey') {
            this.calculatorEnabled = false;
        }
    },

    methods: {
        validatePreview(invalidQuestion) {
            this.$emit('has-questions', !invalidQuestion);
        },

        handleTitleInput(value) {
            // Handle both event objects and direct values from b-input
            const actualValue = typeof value === 'string' ? value : value?.target?.value || '';
            this.title = actualValue;
        },

        handleTitleBlur(event) {
            // Trim whitespace and ensure proper validation
            const trimmedTitle = (event.target.value || '').trim();
            if (trimmedTitle !== this.title) {
                this.title = trimmedTitle;
            }
            // Force reactivity update to parent component
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        get(fieldName) {
            const getterFunction = this.$store.getters[`QuizBuilder/${this.uuid}/get`];
            return getterFunction ? getterFunction(fieldName) : null;
        },

        handleDrop() {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/reorder`, {
                field: 'display_order',
                type: 'questions',
                payload: this.questions,
            });
        },

        set({ field: fieldName, value }) {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/setQuizField`, {
                field: fieldName,
                value,
            });
        },

        async syncGroups(items) {
            this.setGroups('groups', items);

            await this.$store.dispatch('Quizzes/syncGroups', { id: this.id });
        },

        async removeGroups(item) {
            this.setGroups(
                'groups',
                this.quiz.groups.filter(group => item.id !== group.id)
            );

            await this.$store.dispatch('Quizzes/syncGroups', { id: this.id });
        },

        setGroups(fieldName, value) {
            this.$store.commit(`Quizzes/${this.id}/SET`, {
                field: 'groups',
                value,
            });
        },

        async syncCourseCodes(items) {
            this.set({ field: 'course_codes', value: items });

            await this.$store.dispatch('Quizzes/syncCourseCodes', {
                quiz: {
                    id: this.id,
                    title: this.title,
                    calculator_enabled: this.calculatorEnabled,
                },
                paramKey: 'course_code',
                courseCodes: items.map(x => x.id),
            });
        },

        async removeCourseCodes(item) {
            this.set({
                field: 'course_codes',
                value: this.courseCodes.filter(courseCode => item.id !== courseCode.id),
            });

            await this.$store.dispatch('Quizzes/syncCourseCodes', {
                quiz: {
                    id: this.id,
                    title: this.title,
                    calculator_enabled: this.calculatorEnabled,
                },

                paramKey: 'delete_course_code',
                courseCodes: [item.id],
            });
        },
    },
};
</script>
